import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import AdminService from '../../services/adminService';
import PackagesManager from './PackagesManager';
import LoanOptionsManager from './LoanOptionsManager';

/**
 * Pathway Form Component
 *
 * Comprehensive form for creating and editing immigration pathways:
 * - Basic pathway information (name, key, costs, processing time)
 * - Cost components configuration (tuition, living costs, insurance, etc.)
 * - Scoring criteria setup with configurable rules
 * - Multi-language support for pathway descriptions
 * - Real-time validation and error handling
 */
// Ravana charges service fees in LKR for specific markets; extend mapping as new billing currencies roll out
const SERVICE_CURRENCY_BY_COUNTRY = {
  DEU: 'LKR',
  CAN: 'LKR',
  GBR: 'LKR'
};

const getServiceCurrencyLabel = (countryCode) => {
  if (!countryCode) return 'USD';
  const normalized = countryCode.toUpperCase();
  if (SERVICE_CURRENCY_BY_COUNTRY[normalized]) {
    return SERVICE_CURRENCY_BY_COUNTRY[normalized];
  }
  switch (normalized) {
    case 'AUS':
      return 'AUD';
    case 'USA':
      return 'USD';
    default:
      return 'USD';
  }
};

const getCostComponentCurrency = (countryCode) => {
  if (!countryCode) return 'USD';
  const normalized = countryCode.toUpperCase();
  switch (normalized) {
    case 'DEU':
      return 'EUR';
    case 'CAN':
      return 'CAD';
    case 'GBR':
      return 'GBP';
    case 'AUS':
      return 'AUD';
    case 'USA':
      return 'USD';
    default:
      return 'USD';
  }
};

const PathwayForm = ({ pathway, country, onSubmit, onCancel }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    pathway_key: '',
    base_cost: 0,
    processing_days: 0,
    success_rate: 0,
    is_active: true,
    sort_order: 0,
    description: {
      en: '',
      ta: '',
      si: ''
    },
    requirements: {
      en: '',
      ta: '',
      si: ''
    },
    packages_enabled: false,
    loan_enabled: false,
    loan_down_payment_percentage: 25,
    loan_interest_rate: 0,
    loan_repayment_period_years: 5
  });

  const [costComponents, setCostComponents] = useState([]);
  const [scoringCriteria, setScoringCriteria] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const serviceCurrency = getServiceCurrencyLabel(country?.code);

  const commonPathwayTypes = [
    'student_visa',
    'work_visa',
    'chancenkarte',
    'job_seeker_visa',
    'family_visa',
    'startup_visa',
    'research_visa',
    'language_visa',
    'au_pair_visa',
    'volunteer_visa'
  ];

  const costComponentTypes = [
    { value: 'application_fee', label: 'Application Fee', currency: true },
    { value: 'tuition', label: 'Tuition Fees', currency: true },
    { value: 'living_costs', label: 'Living Costs', currency: true },
    { value: 'health_insurance', label: 'Health Insurance', currency: true },
    { value: 'travel_expenses', label: 'Travel Expenses', currency: true },
    { value: 'accommodation', label: 'Accommodation', currency: true },
    { value: 'books_materials', label: 'Books & Materials', currency: true },
    { value: 'visa_extension', label: 'Visa Extension', currency: true },
    { value: 'language_course', label: 'Language Course', currency: true },
    { value: 'other', label: 'Other Costs', currency: true }
  ];

  const scoringCriteriaTypes = [
    { key: 'education', label: 'Education', maxPoints: 35, weight: 0.35 },
    { key: 'language', label: 'Language Skills', maxPoints: 30, weight: 0.30 },
    { key: 'experience', label: 'Work Experience', maxPoints: 20, weight: 0.20 },
    { key: 'age', label: 'Age', maxPoints: 10, weight: 0.10 },
    { key: 'financial', label: 'Financial Capacity', maxPoints: 5, weight: 0.05 },
    { key: 'adaptability', label: 'Adaptability', maxPoints: 15, weight: 0.15 },
    { key: 'job_offer', label: 'Job Offer', maxPoints: 25, weight: 0.25 },
    { key: 'regional_area', label: 'Regional Area', maxPoints: 5, weight: 0.05 }
  ];

  useEffect(() => {
    if (pathway) {
      setFormData({
        id: pathway.id,
        pathway_key: pathway.pathway_key || '',
        base_cost: pathway.base_cost || 0,
        processing_days: pathway.processing_days || 0,
        success_rate: pathway.success_rate || 0,
        is_active: pathway.is_active !== false,
        sort_order: pathway.sort_order || 0,
        description: {
          en: pathway.description?.en || '',
          ta: pathway.description?.ta || '',
          si: pathway.description?.si || ''
        },
        requirements: {
          en: pathway.requirements?.en || '',
          ta: pathway.requirements?.ta || '',
          si: pathway.requirements?.si || ''
        },
        packages_enabled: pathway.packages_enabled || false,
        loan_enabled: pathway.loan_enabled || false,
        loan_down_payment_percentage: pathway.loan_down_payment_percentage || 25,
        loan_interest_rate: pathway.loan_interest_rate || 0,
        loan_repayment_period_years: pathway.loan_repayment_period_years || 5
      });

      // Load existing cost components and scoring criteria if editing
      if (pathway.id) {
        loadPathwayData(pathway.id);
      }
    }
  }, [pathway]);

  const loadPathwayData = async (pathwayId) => {
    try {
      const [costsData, criteriaData] = await Promise.all([
        AdminService.getCostComponents(pathwayId),
        AdminService.getScoringCriteria(pathwayId)
      ]);

      setCostComponents(costsData || []);
      setScoringCriteria(criteriaData || []);
    } catch (error) {
      console.error('Error loading pathway data:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDescriptionChange = (language, value) => {
    setFormData(prev => ({
      ...prev,
      description: {
        ...prev.description,
        [language]: value
      }
    }));
  };

  const handleRequirementsChange = (language, value) => {
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        [language]: value
      }
    }));
  };

  const addCostComponent = () => {
    setCostComponents(prev => [
      ...prev,
      {
        component_type: 'application_fee',
        cost_range_min: 0,
        cost_range_max: 0,
        currency: getCostComponentCurrency(country?.code),
        is_mandatory: true
      }
    ]);
  };

  const updateCostComponent = (index, field, value) => {
    setCostComponents(prev => {
      const updated = [...prev];
      const normalizedValue = (() => {
        if (field.includes('cost')) {
          return parseFloat(value) || 0;
        }
        if (field === 'currency') {
          return (value || serviceCurrency || 'LKR').toUpperCase();
        }
        return value;
      })();
      updated[index] = {
        ...updated[index],
        [field]: normalizedValue
      };
      return updated;
    });
  };

  const removeCostComponent = (index) => {
    setCostComponents(prev => prev.filter((_, i) => i !== index));
  };

  const addScoringCriteria = () => {
    const selectedType = scoringCriteriaTypes[0];
    setScoringCriteria(prev => [
      ...prev,
      {
        criteria_key: selectedType.key,
        max_points: selectedType.maxPoints,
        weight: selectedType.weight,
        is_active: true,
        sort_order: prev.length
      }
    ]);
  };

  const updateScoringCriteria = (index, field, value) => {
    setScoringCriteria(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: field.includes('max_points') || field.includes('weight') ? parseFloat(value) || 0 : value
      };
      return updated;
    });
  };

  const removeScoringCriteria = (index) => {
    setScoringCriteria(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    // Basic information validation
    if (!formData.pathway_key.trim()) {
      newErrors.pathway_key = 'Pathway key is required';
    }

    if (formData.base_cost < 0) {
      newErrors.base_cost = 'Base cost must be non-negative';
    }

    if (formData.processing_days < 0) {
      newErrors.processing_days = 'Processing days must be non-negative';
    }

    if (formData.success_rate < 0 || formData.success_rate > 100) {
      newErrors.success_rate = 'Success rate must be between 0 and 100';
    }

    // Description validation
    const hasDescription = Object.values(formData.description).some(desc => desc.trim());
    if (!hasDescription) {
      newErrors.description = 'At least one language description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const pathwayData = {
        ...formData,
        cost_components: costComponents,
        scoring_criteria: scoringCriteria
      };

      await onSubmit(pathwayData);
    } catch (error) {
      console.error('Error saving pathway:', error);
      setErrors({
        submit: 'Failed to save pathway. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const costComponentCurrencyOptions = Array.from(new Set([
    serviceCurrency,
    'LKR',
    'EUR',
    'USD',
    'GBP',
    'CAD',
    'AUD'
  ].filter(Boolean))).map(code => code.toUpperCase());

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-white">
          {pathway ? 'Edit Pathway' : 'Create New Pathway'}
          <span className="text-sm text-gray-400 ml-2">
            ({country?.flag_emoji} {country?.name})
          </span>
        </h3>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-white transition-colors duration-150"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700 mb-6">
        <nav className="flex space-x-8" aria-label="Tabs">
          {[
            { key: 'basic', label: 'Basic Info' },
            { key: 'costs', label: 'Cost Components' },
            { key: 'scoring', label: 'Scoring Criteria' },
            { key: 'packages', label: 'Packages' },
            { key: 'loans', label: 'Loan Options' },
            { key: 'translations', label: 'Translations' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === tab.key
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Basic Information Tab */}
        {activeTab === 'basic' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Pathway Type <span className="text-red-500">*</span>
              </label>
              <select
                name="pathway_key"
                value={formData.pathway_key}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.pathway_key ? 'border-red-500' : 'border-gray-600'
                  }`}
              >
                <option value="">Select pathway type</option>
                {commonPathwayTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ').toUpperCase()}
                  </option>
                ))}
              </select>
              {errors.pathway_key && (
                <p className="mt-1 text-sm text-red-400">{errors.pathway_key}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Base Cost ({serviceCurrency})
                </label>
                <input
                  type="number"
                  name="base_cost"
                  value={formData.base_cost}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.base_cost ? 'border-red-500' : 'border-gray-600'
                    }`}
                  min="0"
                  step="0.01"
                />
                {errors.base_cost && (
                  <p className="mt-1 text-sm text-red-400">{errors.base_cost}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Processing Days
                </label>
                <input
                  type="number"
                  name="processing_days"
                  value={formData.processing_days}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.processing_days ? 'border-red-500' : 'border-gray-600'
                    }`}
                  min="0"
                />
                {errors.processing_days && (
                  <p className="mt-1 text-sm text-red-400">{errors.processing_days}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Success Rate (%)
                </label>
                <input
                  type="number"
                  name="success_rate"
                  value={formData.success_rate}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.success_rate ? 'border-red-500' : 'border-gray-600'
                    }`}
                  min="0"
                  max="100"
                  step="0.1"
                />
                {errors.success_rate && (
                  <p className="mt-1 text-sm text-red-400">{errors.success_rate}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
                />
                <span className="ml-2 text-sm font-medium text-gray-300">
                  Active Pathway
                </span>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  name="sort_order"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  className="w-24 px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
            </div>
          </div>
        )}

        {/* Cost Components Tab */}
        {activeTab === 'costs' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-md font-medium text-white">Cost Components</h4>
              <button
                type="button"
                onClick={addCostComponent}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Add Cost Component
              </button>
            </div>

            {costComponents.length === 0 ? (
              <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-600 rounded-lg">
                No cost components added yet. Click "Add Cost Component" to get started.
              </div>
            ) : (
              <div className="space-y-3">
                {costComponents.map((component, index) => (
                  <div key={index} className="p-4 border border-gray-700 rounded-lg bg-gray-800">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Type</label>
                        <select
                          value={component.component_type}
                          onChange={(e) => updateCostComponent(index, 'component_type', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-600 rounded bg-gray-700 text-white"
                        >
                          {costComponentTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Min Cost</label>
                        <input
                          type="number"
                          value={component.cost_range_min}
                          onChange={(e) => updateCostComponent(index, 'cost_range_min', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-600 rounded bg-gray-700 text-white"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Max Cost</label>
                        <input
                          type="number"
                          value={component.cost_range_max}
                          onChange={(e) => updateCostComponent(index, 'cost_range_max', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-600 rounded bg-gray-700 text-white"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Currency</label>
                        <select
                          value={(component.currency || serviceCurrency || 'LKR').toUpperCase()}
                          onChange={(e) => updateCostComponent(index, 'currency', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-600 rounded bg-gray-700 text-white"
                        >
                          {costComponentCurrencyOptions.map(currencyCode => (
                            <option key={currencyCode} value={currencyCode}>{currencyCode}</option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-center space-x-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={component.is_mandatory}
                            onChange={(e) => updateCostComponent(index, 'is_mandatory', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
                          />
                          <span className="ml-1 text-xs text-gray-300">Mandatory</span>
                        </label>

                        <button
                          type="button"
                          onClick={() => removeCostComponent(index)}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Scoring Criteria Tab */}
        {activeTab === 'scoring' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-md font-medium text-white">Scoring Criteria</h4>
              <button
                type="button"
                onClick={addScoringCriteria}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Add Scoring Criteria
              </button>
            </div>

            {scoringCriteria.length === 0 ? (
              <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-600 rounded-lg">
                No scoring criteria added yet. Click "Add Scoring Criteria" to get started.
              </div>
            ) : (
              <div className="space-y-3">
                {scoringCriteria.map((criteria, index) => (
                  <div key={index} className="p-4 border border-gray-700 rounded-lg bg-gray-800">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Criteria</label>
                        <select
                          value={criteria.criteria_key}
                          onChange={(e) => updateScoringCriteria(index, 'criteria_key', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-600 rounded bg-gray-700 text-white"
                        >
                          {scoringCriteriaTypes.map((type) => (
                            <option key={type.key} value={type.key}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Max Points</label>
                        <input
                          type="number"
                          value={criteria.max_points}
                          onChange={(e) => updateScoringCriteria(index, 'max_points', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-600 rounded bg-gray-700 text-white"
                          min="0"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Weight</label>
                        <input
                          type="number"
                          value={criteria.weight}
                          onChange={(e) => updateScoringCriteria(index, 'weight', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-600 rounded bg-gray-700 text-white"
                          min="0"
                          max="1"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Sort Order</label>
                        <input
                          type="number"
                          value={criteria.sort_order}
                          onChange={(e) => updateScoringCriteria(index, 'sort_order', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-600 rounded bg-gray-700 text-white"
                          min="0"
                        />
                      </div>

                      <div className="flex items-center space-x-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={criteria.is_active}
                            onChange={(e) => updateScoringCriteria(index, 'is_active', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
                          />
                          <span className="ml-1 text-xs text-gray-300">Active</span>
                        </label>

                        <button
                          type="button"
                          onClick={() => removeScoringCriteria(index)}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Packages Tab */}
        {activeTab === 'packages' && (
          <PackagesManager
            pathway={{ ...formData, service_currency: serviceCurrency }}
            serviceCurrency={serviceCurrency}
            onUpdate={(updates) => {
              setFormData(prev => ({ ...prev, ...updates }));
            }}
          />
        )}

        {/* Loan Options Tab */}
        {activeTab === 'loans' && (
          <LoanOptionsManager
            pathway={formData}
            onUpdate={(updates) => {
              setFormData(prev => ({ ...prev, ...updates }));
            }}
          />
        )}

        {/* Translations Tab */}
        {activeTab === 'translations' && (
          <div className="space-y-6">
            <div>
              <h4 className="text-md font-medium text-white mb-4">Pathway Descriptions</h4>
              {['en', 'ta', 'si'].map((lang) => (
                <div key={lang} className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description ({lang === 'en' ? 'English' : lang === 'ta' ? 'Tamil (தமிழ்)' : 'Sinhala (සිංහල)'})
                  </label>
                  <textarea
                    value={formData.description[lang]}
                    onChange={(e) => handleDescriptionChange(lang, e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                    placeholder={`Enter pathway description in ${lang === 'en' ? 'English' : lang === 'ta' ? 'Tamil' : 'Sinhala'}`}
                  />
                </div>
              ))}
            </div>

            <div>
              <h4 className="text-md font-medium text-white mb-4">Requirements</h4>
              {['en', 'ta', 'si'].map((lang) => (
                <div key={lang} className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Requirements ({lang === 'en' ? 'English' : lang === 'ta' ? 'Tamil (தமிழ்)' : 'Sinhala (සිංහල)'})
                  </label>
                  <textarea
                    value={formData.requirements[lang]}
                    onChange={(e) => handleRequirementsChange(lang, e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                    placeholder={`Enter pathway requirements in ${lang === 'en' ? 'English' : lang === 'ta' ? 'Tamil' : 'Sinhala'}`}
                  />
                </div>
              ))}
            </div>

            {errors.description && (
              <div className="bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-lg">
                {errors.description}
              </div>
            )}
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            <span>{pathway ? 'Update Pathway' : 'Create Pathway'}</span>
          </button>
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </div>
    </div>
  );
};

export default PathwayForm;
